<template>
  <PickLotteryQuickPickCard
    v-model:collapsed="isRandom"
    class="pick-lottery-quick-pick-wager"
    :headline="$t('picklottery.quickpick.wager.title')"
  >
    <ChargeSelector
      v-model:value="modelValue"
      :aria-label="$t('picklottery.stake.slider.arialabel')"
      :charges="preparedWagers"
      class="slider"
    />
  </PickLotteryQuickPickCard>
</template>

<script lang="ts" setup>
import type {
  PriceListPayload,
  Stake,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { QuickPickSettings } from '../stores/lottery'

import { isFixedCharge } from '../utils/lotteryCache'

type PickLotteryQuickPickWagerEmits = {
  (event: 'update:value', value: PickLotteryQuickPickWagerProps['value']): void
}

type PickLotteryQuickPickWagerProps = {
  priceList: PriceListPayload
  stakes?: Stake[]
  value?: QuickPickSettings['wager']
}

const emit = defineEmits<PickLotteryQuickPickWagerEmits>()
const { priceList, stakes, value = null } = defineProps<PickLotteryQuickPickWagerProps>()

const currentStake = computed(
  () => fixedStakes.value.find((charge) => charge.value.value === value) ?? null,
)
const fixedStakes = computed(() => priceList.charges.filter(isFixedCharge))
const isRandom = computed({
  get: () => !value,
  set: (newValue) => {
    if (newValue || !stakes?.length) {
      emit('update:value', null)
      return
    }

    emit('update:value', getWagerAmount(stakes[0].price) ?? null)
  },
})
const modelValue = computed({
  get: () => currentStake.value?.id ?? '',
  set: (newValue) => {
    emit('update:value', getWagerAmount(newValue) ?? null)
  },
})

const preparedWagers = computed(
  () =>
    stakes?.map((stake) => ({
      id: stake.price,
      value: getWagerAmount(stake.price) ?? 0,
    })) ?? [],
)

const getWagerAmount = (selectedStake?: Maybe<string>) =>
  fixedStakes.value.find((stake) => stake.id === selectedStake)?.value.value
</script>

<style lang="scss" scoped>
.pick-lottery-quick-pick-wager {
  .slider {
    :deep(.slider-base) {
      background-color: token('color/background/secondary/secondary');
    }
  }
}
</style>
